import React from 'react';
import { getTradeIconForTrade, getCustomTradeIconSrc } from '@/lib/tradeUtils';
import { Tradesperson } from '@/lib/types';

interface TradeIconProps {
  tradeType?: string | null;
  tradesperson?: Tradesperson;
  className?: string;
  alt?: string;
  onError?: () => void;
  /** For category buttons, applies special styling for custom icons */
  categoryButton?: boolean;
  /** Whether the category button is active (selected) */
  isActive?: boolean;
}

/**
 * Universal trade icon component that handles both custom WebP icons and Lucide fallbacks
 *
 * Usage:
 * - <TradeIcon tradeType="plumber" className="h-6 w-6" />
 * - <TradeIcon tradesperson={tradeData} className="h-8 w-8" />
 */
export function TradeIcon({
  tradeType,
  tradesperson,
  className = "h-6 w-6",
  alt,
  onError,
  categoryButton = false,
  isActive = false
}: TradeIconProps) {
  // Determine the trade type to use
  const effectiveTradeType = tradeType || tradesperson?.trade_type;

  // Get custom icon source if available
  const customIconSrc = getCustomTradeIconSrc(effectiveTradeType);

  // Get Lucide icon as fallback
  const LucideIcon = getTradeIconForTrade(tradesperson || { trade_type: effectiveTradeType } as Tradesperson);

  // For category buttons with custom icons, we need special handling
  if (categoryButton && customIconSrc) {
    return (
      <div className="relative">
        <img
          src={customIconSrc}
          alt={alt || `${effectiveTradeType} icon`}
          className={className}
          onError={(e) => {
            // Hide the image and show the fallback Lucide icon
            e.currentTarget.style.display = 'none';
            const fallback = e.currentTarget.nextElementSibling as HTMLElement;
            if (fallback) {
              fallback.classList.remove('hidden');
            }
            onError?.();
          }}
        />
        <LucideIcon
          className={`${className} hidden ${isActive ? 'text-white' : 'text-purple-700'}`}
        />
      </div>
    );
  }

  // If we have a custom icon for non-category usage, render it with fallback to Lucide
  if (customIconSrc) {
    return (
      <div className={`relative`}>
        <img
          src={customIconSrc}
          alt={alt || `${effectiveTradeType} icon`}
          className={className}
          onError={(e) => {
            // Hide the image and show the fallback Lucide icon
            e.currentTarget.style.display = 'none';
            const fallback = e.currentTarget.nextElementSibling as HTMLElement;
            if (fallback) {
              fallback.classList.remove('hidden');
            }
            onError?.();
          }}
        />
        <LucideIcon
          className={`${className} hidden text-purple-700`}
        />
      </div>
    );
  }

  // Default to Lucide icon
  return <LucideIcon className={`${className} text-purple-700`} />;
}
